import { useState } from "react";
import { MdRefresh } from "react-icons/md";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { Modal } from "react-bootstrap";
import { PulseLoader } from "react-spinners";
import {
  generateNextSequenceQuestion,
  createNextSequenceQuestion,
} from "../../../api/common";
import styles from "../style.module.scss";

interface QuestionPreviewModalProps {
  show: boolean;
  onHide: () => void;
  auditId: number;
  activeSequence: number;
  questionRowData: any;
  auditDetails: any;
  onQuestionsSent: () => void;
  onError: (message: string) => void;
}

const QuestionPreviewModal = ({
  show,
  onHide,
  auditId,
  activeSequence,
  questionRowData,
  auditDetails,
  onQuestionsSent,
  onError,
}: QuestionPreviewModalProps) => {
  const [questionGenerating, setQuestionGenerating] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [sendingQuestions, setSendingQuestions] = useState(false);
  const [previewQuestions, setPreviewQuestions] = useState<
    {
      id: number;
      question: string;
      purpose: string;
      artifacts: string;
      regulatoryFrameworkId: number | null;
      themeId: number | null;
      selected: boolean;
      isCustom: boolean;
    }[]
  >([]);
  const [customQuestion, setCustomQuestion] = useState("");
  const [editingQuestionId, setEditingQuestionId] = useState<number | null>(
    null,
  );
  const [editingQuestionText, setEditingQuestionText] = useState("");

  // Generate questions when modal opens
  const handleGenerateQuestions = async () => {
    setQuestionGenerating(true);
    setPreviewQuestions([]);
    setCustomQuestion("");

    try {
      const response = await generateNextSequenceQuestion({
        auditId,
      });
      if (response?.questions) {
        const aiQuestions = response.questions.map((q: any, index: number) => ({
          id: Date.now() + index,
          question: q.question,
          purpose: q.purpose || "",
          artifacts: q.artifacts || "",
          regulatoryFrameworkId: q.regulatoryFrameworkId || null,
          selected: false,
          isCustom: false,
        }));
        setPreviewQuestions(aiQuestions);
      }
    } catch (error) {
      console.error("Error generating questions:", error);
    } finally {
      setQuestionGenerating(false);
    }
  };

  // Toggle question selection
  const handleToggleQuestion = (questionId: number) => {
    setPreviewQuestions((prev) =>
      prev.map((q) =>
        q.id === questionId ? { ...q, selected: !q.selected } : q,
      ),
    );
  };

  // Add custom question
  const handleAddCustomQuestion = () => {
    if (customQuestion.trim()) {
      const newQuestion = {
        id: Date.now(),
        question: customQuestion.trim(),
        purpose: "",
        artifacts: "",
        regulatoryFrameworkId: null,
        themeId: null,
        selected: true,
        isCustom: true,
      };
      setPreviewQuestions((prev) => [...prev, newQuestion]);
      setCustomQuestion("");
    }
  };

  // Delete question
  const handleDeleteQuestion = (questionId: number) => {
    setPreviewQuestions((prev) => prev.filter((q) => q.id !== questionId));
  };

  // Start editing a question
  const handleStartEditQuestion = (
    questionId: number,
    questionText: string,
  ) => {
    setEditingQuestionId(questionId);
    setEditingQuestionText(questionText);
  };

  // Save edited question
  const handleSaveEditQuestion = () => {
    if (editingQuestionId && editingQuestionText.trim()) {
      setPreviewQuestions((prev) =>
        prev.map((q) =>
          q.id === editingQuestionId
            ? { ...q, question: editingQuestionText.trim() }
            : q,
        ),
      );
      setEditingQuestionId(null);
      setEditingQuestionText("");
    }
  };

  // Cancel editing
  const handleCancelEditQuestion = () => {
    setEditingQuestionId(null);
    setEditingQuestionText("");
  };

  // Regenerate AI questions
  const handleRegenerateQuestions = async () => {
    setQuestionGenerating(true);
    setIsRegenerating(true);
    // Keep custom questions
    const customQuestions = previewQuestions.filter((q) => q.isCustom);

    try {
      const response = await generateNextSequenceQuestion({
        auditId,
      });
      if (response?.questions) {
        const aiQuestions = response.questions.map((q: any, index: number) => ({
          id: Date.now() + index,
          question: q.question,
          purpose: q.purpose || "",
          artifacts: q.artifacts || "",
          regulatoryFrameworkId: q.regulatoryFrameworkId || null,
          selected: false,
          isCustom: false,
        }));
        setPreviewQuestions([...aiQuestions, ...customQuestions]);
      }
    } catch (error) {
      console.error("Error regenerating questions:", error);
    } finally {
      setQuestionGenerating(false);
      setIsRegenerating(false);
    }
  };

  // Send questions
  const handleSendQuestions = async () => {
    const selectedQuestions = previewQuestions.filter((q) => q.selected);
    setSendingQuestions(true);

    try {
      const payload = {
        auditId,
        confirmedQuestions: selectedQuestions.map((q) => ({
          question: q.question,
          purpose: q.purpose,
          artifacts: q.artifacts,
          regulatoryFrameworkId: q.regulatoryFrameworkId,
          themeId: q.themeId,
        })),
      };

      await createNextSequenceQuestion(payload);
      onHide();
      onQuestionsSent();
    } catch (error) {
      console.error("Error sending questions:", error);
      onError("Failed to send questions. Please try again.");
    } finally {
      setSendingQuestions(false);
    }
  };

  // Render a question item (shared between AI and custom sections)
  const renderQuestionItem = (question: {
    id: number;
    question: string;
    selected: boolean;
    isCustom: boolean;
  }) => (
    <div key={question.id} className={styles.questionItem}>
      {editingQuestionId === question.id ? (
        <div className={styles.editQuestionRow}>
          <input
            type="text"
            className={styles.editQuestionInput}
            value={editingQuestionText}
            onChange={(e) => setEditingQuestionText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSaveEditQuestion();
              } else if (e.key === "Escape") {
                handleCancelEditQuestion();
              }
            }}
            autoFocus
          />
          <div className={styles.editQuestionActions}>
            <button
              className={styles.saveEditBtn}
              onClick={handleSaveEditQuestion}
              disabled={!editingQuestionText.trim()}
            >
              Save
            </button>
            <button
              className={styles.cancelEditBtn}
              onClick={handleCancelEditQuestion}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <label className={styles.questionCheckbox}>
            <input
              type="checkbox"
              checked={question.selected}
              onChange={() => handleToggleQuestion(question.id)}
            />
            <span className={styles.questionText}>{question.question}</span>
          </label>
          <div className={styles.questionActions}>
            <button
              className={styles.editBtn}
              onClick={() =>
                handleStartEditQuestion(question.id, question.question)
              }
            >
              <FiEdit2 size={16} />
            </button>
            <button
              className={styles.deleteBtn}
              onClick={() => handleDeleteQuestion(question.id)}
            >
              <FiTrash2 size={16} />
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      onEnter={handleGenerateQuestions}
      centered
      size="lg"
      className={styles.questionPreviewModal}
    >
      <Modal.Header closeButton className={styles.questionPreviewHeader}>
        <Modal.Title>
          Question Preview - Step {activeSequence + 1}:{" "}
          {questionRowData?.sequences?.[activeSequence]?.sequenceNames?.join(
            ", ",
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.questionPreviewBody}>
        {questionGenerating ? (
          <div className="text-center" style={{ padding: "40px 0" }}>
            <PulseLoader size={8} color="#0a2a63" />
            <p style={{ marginTop: "16px", color: "#6b7280" }}>
              {isRegenerating
                ? "Regenerating questions..."
                : "Generating questions..."}
            </p>
          </div>
        ) : (
          <>
            {/* AI Generated Questions Section */}
            <div className={styles.aiQuestionsSection}>
              <div className={styles.aiQuestionsHeader}>
                <span>
                  AI-Generated Questions (
                  {previewQuestions.filter((q) => !q.isCustom).length})
                </span>
                <button
                  className={styles.regenerateBtn}
                  onClick={handleRegenerateQuestions}
                  disabled={questionGenerating}
                >
                  <MdRefresh size={16} />
                  Regenerate
                </button>
              </div>

              <div className={styles.questionsList}>
                {previewQuestions
                  .filter((q) => !q.isCustom)
                  .map((question) => renderQuestionItem(question))}
                {previewQuestions.filter((q) => !q.isCustom).length === 0 && (
                  <p className={styles.noQuestionsText}>
                    No AI-generated questions available.
                  </p>
                )}
              </div>
            </div>

            {/* Custom Questions Section */}
            {previewQuestions.filter((q) => q.isCustom).length > 0 && (
              <div className={styles.customQuestionsSection}>
                <div className={styles.customQuestionsHeader}>
                  <span>
                    Custom Questions (
                    {previewQuestions.filter((q) => q.isCustom).length})
                  </span>
                </div>

                <div className={styles.questionsList}>
                  {previewQuestions
                    .filter((q) => q.isCustom)
                    .map((question) => renderQuestionItem(question))}
                </div>
              </div>
            )}

            {/* Add Custom Question Section */}
            <div className={styles.addCustomQuestionSection}>
              <label className={styles.customQuestionLabel}>
                Add Custom Question:
              </label>
              <div className={styles.customQuestionInput}>
                <input
                  type="text"
                  placeholder="Enter your custom follow-up question..."
                  value={customQuestion}
                  onChange={(e) => setCustomQuestion(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleAddCustomQuestion();
                    }
                  }}
                />
                <button
                  className={styles.addQuestionBtn}
                  onClick={handleAddCustomQuestion}
                  disabled={!customQuestion.trim()}
                >
                  +
                </button>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className={styles.questionPreviewFooter}>
        <button
          className={styles.cancelBtn}
          onClick={onHide}
          disabled={sendingQuestions}
        >
          Cancel
        </button>
        <button
          className={styles.sendQuestionsModalBtn}
          onClick={handleSendQuestions}
          disabled={
            !previewQuestions.some((q) => q.selected) ||
            sendingQuestions ||
            questionGenerating
          }
        >
          {sendingQuestions ? "Sending..." : "Send Questions"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuestionPreviewModal;
